// Automatic promotions applied to a sale by rule (F-POS-03; spec/06 §8.4, spec/13 §4).
//
// An **automatic promotion** discounts a cart without a code being entered: it applies by rule — a
// day, a time-of-day window (happy hour), a line-kind or product set, a cart threshold, a channel,
// a venue, the player's first order (spec/06 §8.4). Unlike a discount code (F-POS-02) it carries no
// `code`; the Sell screen applies the best applicable one automatically when the sale is placed and
// shows it on the cart (spec/13 §4).
//
// A promotion is organisation-scoped: it is defined once for the organisation and names the venues
// and channels it applies at. Its money value settles to the ledger's `discounts` contra-revenue
// account on the sale that used it (spec/06 §2.1). This build supports the two cart-discount types —
// `percent` and `amount`; `fixed_price`, `free_item` and `bonus_time` (spec/06 §8.4) ride in with the
// line-replacement and time-grant fulfilment they need.
//
//   POST  …/promotions        define an automatic promotion (discounts:manage)
//   GET   …/promotions        the organisation's promotions (discounts:manage)
//   PATCH …/promotions/{id}   edit one — value, rules, active (discounts:manage)
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { PromotionIdSchema, ProductIdSchema, StationClassIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { NonNegativeMoneySchema } from "../primitives/money.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { SaleChannelSchema } from "./commerce.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { DISCOUNTS_MANAGE } from "../permissions/index.js";
/**
 * What an automatic promotion takes off the cart (spec/06 §8.4). `percent` takes a share of the
 * applicable lines' value; `amount` takes a fixed sum. `console` is a **console offer** (ADR-0034, spec/06 §8.4
 * rule 8): it never touches a cart or play by the minute — it discounts a staff-run session's one order, by the
 * conditions and reward in `console`. The other spec types (`fixed_price`, `free_item`, `bonus_time`) replace a line
 * or grant time and ride in with that fulfilment.
 */
export const PromotionTypeSchema = z
    .enum(['percent', 'amount', 'console'])
    .openapi('PromotionType', { description: 'How an automatic promotion discounts a cart (spec/06 §8.4).' });
/**
 * What a promotion discounts (spec/06 §8.4). The four sale-line kinds (`time`, `food`, `retail`, `other`) are
 * discounted on a sale when it is placed; `play` is **pay-as-you-go and wallet play** — the session's play charged
 * per billing unit at the class rate (spec/05 §4.3) — discounted unit by unit as each unit starts (a happy hour on
 * play time). `time` keeps its meaning: the `time` lines of a sale (a pass, a counter time sale), never play.
 *
 * A `play` promotion stands alone: `appliesToKinds` is exactly `['play']`, its type is `percent`, and the rules that
 * belong to a cart — a product set, a channel, a minimum order, a first order — are refused (spec/06 §8.4 rule 7).
 * An empty `appliesToKinds` ("every kind") means every sale-line kind and never `play`, so a promotion made before
 * `play` existed discounts exactly what it did.
 */
export const PromotionKindSchema = z
    .enum(['time', 'food', 'retail', 'other', 'play'])
    .openapi('PromotionKind', { description: 'What an automatic promotion discounts: a sale-line kind, or `play` (pay-as-you-go & wallet play).' });
const NAME = z.string().min(1).max(120);
/** A minute of the venue's day, 0 (00:00) … 1439 (23:59), for a happy-hour window (spec/06 §8.4). */
const MinuteOfDaySchema = z.int().min(0).max(1439);
/** A day of the week, 0 (Sunday) … 6 (Saturday), in the venue's timezone. */
const DayOfWeekSchema = z.int().min(0).max(6);
/** What a console offer gives (ADR-0034, spec/06 §8.4 rule 8). */
export const ConsoleOfferRewardKindSchema = z
    .enum(['extra_controllers_percent', 'extra_controllers_amount', 'bundle', 'free_time', 'session_percent'])
    .openapi('ConsoleOfferRewardKind', {
    description: 'extra_controllers_percent: a share off every controller after the first; extra_controllers_amount: a sum off each extra controller; ' +
        'bundle: N controllers × X minutes for a set price; free_time: book X + Y minutes, Y are free; session_percent: a share off the session.',
});
/**
 * A console offer's conditions and reward (ADR-0034). The day, the time window, the venues, the validity, the cap,
 * the priority and stacking are the promotion's own fields; these are what only a console session has.
 */
export const ConsoleOfferSchema = z
    .object({
    /** At least this many controllers at some point of the session; null for any. */
    minControllers: z.int().min(1).max(16).nullable(),
    /** At least this many minutes booked (or played, when Open); null for any. */
    minDurationMinutes: z.int().min(1).max(1440).nullable(),
    /** The station classes it applies to; empty is every staff-run class. */
    stationClassIds: z.array(StationClassIdSchema).max(50),
    /** Only when a registered player is attached to the session — a walk-in does not get it. */
    playersOnly: z.boolean(),
    reward: ConsoleOfferRewardKindSchema,
    /** `extra_controllers_percent` and `session_percent`: basis points off (1–10000). Null otherwise. */
    percentBps: z.int().min(1).max(10_000).nullable(),
    /** `extra_controllers_amount`: the sum off each extra controller; `bundle`: the bundle's price. Null otherwise. */
    amount: NonNegativeMoneySchema.nullable(),
    /** `bundle`: how many controllers the price is for. Null otherwise. */
    controllers: z.int().min(1).max(16).nullable(),
    /** `bundle`: the minutes the price is for; `free_time`: the minutes paid for. Null otherwise. */
    minutes: z.int().min(1).max(1440).nullable(),
    /** `free_time`: the minutes given free on top of `minutes`. Null otherwise. */
    freeMinutes: z.int().min(1).max(1440).nullable(),
})
    .openapi('ConsoleOffer', { description: 'A console offer: conditions on the controllers, the duration and the class, and its reward (ADR-0034).' });
/** The rules that decide whether an automatic promotion applies to a sale (spec/06 §8.4). */
export const PromotionRulesSchema = z
    .object({
    /**
     * What it discounts: sale-line kinds, or `['play']` alone for pay-as-you-go & wallet play. Empty means every
     * sale-line kind (never `play`).
     */
    appliesToKinds: z.array(PromotionKindSchema).max(5).default([]),
    /** The products it discounts (a product set); empty means every product of the applicable kinds. */
    productIds: z.array(ProductIdSchema).max(500).default([]),
    /** The days of the week it runs; empty means every day. */
    daysOfWeek: z.array(DayOfWeekSchema).max(7).default([]),
    /** The happy-hour window, minutes of the venue's day; both null means all day. */
    startMinuteOfDay: MinuteOfDaySchema.nullable().default(null),
    endMinuteOfDay: MinuteOfDaySchema.nullable().default(null),
    /** The venues it applies at; empty means every venue. Ids, not resolved here. */
    venueIds: z.array(z.string()).max(500).default([]),
    /** The channels it applies on; empty means every channel. */
    channels: z.array(SaleChannelSchema).max(5).default([]),
    /** The smallest cart it applies to (the threshold rule); null means no minimum. */
    minOrderAmount: NonNegativeMoneySchema.nullable().default(null),
    /** Applies only to the player's first paid sale at the organisation. */
    firstOrderOnly: z.boolean().default(false),
})
    .openapi('PromotionRules');
/** An automatic promotion (spec/06 §8.4, F-POS-03). */
export const PromotionSchema = z
    .object({
    id: PromotionIdSchema,
    name: NAME,
    type: PromotionTypeSchema,
    /** For `percent`: basis points off the applicable value (1–10000). Null for `amount`. */
    percentBps: z.int().min(1).max(10_000).nullable(),
    /** For `amount`: the sum off the cart. Null for `percent`. */
    amount: NonNegativeMoneySchema.nullable(),
    /** The most it may take off, whichever type — per sale, or per session for a `play` promotion. Null for no cap. */
    maxDiscountAmount: NonNegativeMoneySchema.nullable(),
    rules: PromotionRulesSchema,
    /** For a `console` promotion: its conditions and reward (ADR-0034). Absent for every other type. */
    console: ConsoleOfferSchema.optional(),
    /** Higher wins when two non-stackable promotions both apply (spec/06 §8.4 rule 1). */
    priority: z.int().min(0).max(1000),
    /** May combine with a code and other stackable promotions (spec/06 §8.4 rule 1). */
    stackable: z.boolean(),
    active: z.boolean(),
    validFrom: IsoDateTimeSchema.nullable(),
    validUntil: IsoDateTimeSchema.nullable(),
    createdAt: IsoDateTimeSchema,
    updatedAt: IsoDateTimeSchema,
})
    .openapi('Promotion');
export const PromotionListSchema = z.object({ promotions: z.array(PromotionSchema) }).openapi('PromotionList');
/**
 * Define an automatic promotion (spec/06 §8.4). Exactly one of `percentBps`/`amount` for its type; a `console`
 * promotion takes `console` instead (its reward carries its own value).
 */
export const CreatePromotionSchema = z
    .object({
    name: NAME,
    type: PromotionTypeSchema,
    percentBps: z.int().min(1).max(10_000).optional(),
    amount: NonNegativeMoneySchema.optional(),
    maxDiscountAmount: NonNegativeMoneySchema.optional(),
    rules: PromotionRulesSchema.optional(),
    console: ConsoleOfferSchema.optional(),
    priority: z.int().min(0).max(1000).optional(),
    stackable: z.boolean().optional(),
    active: z.boolean().optional(),
    validFrom: IsoDateTimeSchema.optional(),
    validUntil: IsoDateTimeSchema.optional(),
})
    .openapi('CreatePromotionRequest');
/** Edit an automatic promotion. Every field is optional; an absent field is left unchanged. */
export const UpdatePromotionSchema = z
    .object({
    name: NAME.optional(),
    percentBps: z.int().min(1).max(10_000).nullable().optional(),
    amount: NonNegativeMoneySchema.nullable().optional(),
    maxDiscountAmount: NonNegativeMoneySchema.nullable().optional(),
    rules: PromotionRulesSchema.optional(),
    /** A `console` promotion's conditions and reward, whole (ADR-0034). Refused on any other type. */
    console: ConsoleOfferSchema.optional(),
    priority: z.int().min(0).max(1000).optional(),
    stackable: z.boolean().optional(),
    active: z.boolean().optional(),
    validFrom: IsoDateTimeSchema.nullable().optional(),
    validUntil: IsoDateTimeSchema.nullable().optional(),
})
    .openapi('UpdatePromotionRequest');
const PromotionParamsSchema = OrganizationScopeParamsSchema.extend({ promotionId: PromotionIdSchema }).openapi('PromotionParams');
export const createPromotionRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/promotions',
    operationId: 'createPromotion',
    tags: ['commerce'],
    summary: 'Define an automatic promotion (F-POS-03)',
    description: 'Defines a promotion that applies to a sale by rule. Requires `discounts:manage`. Audited.',
    [PERMISSION_EXTENSION]: DISCOUNTS_MANAGE,
    request: {
        params: OrganizationScopeParamsSchema,
        body: { content: { 'application/json': { schema: CreatePromotionSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(PromotionSchema, 'The promotion.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation.', 'not-found'),
        409: problemResponse('The value does not match the type, the window is invalid, a `play` promotion carries a rule play cannot take, or a `console` offer’s reward is incomplete.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const listPromotionsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/promotions',
    operationId: 'listPromotions',
    tags: ['commerce'],
    summary: 'List the organisation’s promotions (F-POS-03)',
    [PERMISSION_EXTENSION]: DISCOUNTS_MANAGE,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(PromotionListSchema, 'The organisation’s promotions.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const updatePromotionRoute = createRoute({
    method: 'patch',
    path: '/v1/organizations/{organizationId}/promotions/{promotionId}',
    operationId: 'updatePromotion',
    tags: ['commerce'],
    summary: 'Edit an automatic promotion (F-POS-03)',
    description: 'Edits a promotion’s value, rules or active state. Requires `discounts:manage`. Audited.',
    [PERMISSION_EXTENSION]: DISCOUNTS_MANAGE,
    request: {
        params: PromotionParamsSchema,
        body: { content: { 'application/json': { schema: UpdatePromotionSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(PromotionSchema, 'The updated promotion.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such promotion.', 'not-found'),
        409: problemResponse('The value does not match the type, the window is invalid, a `play` promotion carries a rule play cannot take, or a `console` offer’s reward is incomplete.', 'conflict'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=promotions.js.map